import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ResponsiveDialog from '../common/ResponsiveDialog';
import apiService from '../../services/api.service';
import { generateSapPassword } from '../../utils/sapPassword';

/**
 * Settings > SAP Approval Workflows > SAP B1 passwords (Admin). The SAP B1 password of every
 * active PRPO user with a SAP user (Users.SAPUserCode): PRPO's integration user (a superuser) sets
 * it in SAP, PRPO checks it with one login as the user and saves it encrypted on THIS server for
 * SAP approvals; or, when it is already set in SAP (from another PRPO server), PRPO only checks
 * and saves it. SAP keeps a password per company database, so the page sends one request per
 * company ticked (backend/src/services/sapUserPasswords.service.ts).
 */

interface SavedInfo {
  credentialId: number;
  status: string;
  lastUsed: string | null;
  updated: string;
  verifiedCompany: string | null;
  lastError: string | null;
}
interface UserRow {
  userId: number;
  username: string;
  fullname: string;
  email: string;
  sapUserCode: string;
  protectedReason: string;
  saved: SavedInfo | null;
  lastChange: { action: string; at: string; by: string | null } | null;
}
interface CompanyRow { id: number; name: string; database: string; sapApproval: boolean }
interface Data {
  technicalUser: string;
  canSave: boolean;
  companies: CompanyRow[];
  users: UserRow[];
  orphans: Array<SavedInfo & { sapUserCode: string; linkedName: string | null }>;
}
type Mode = 'set' | 'save';
type Outcome = 'Pending' | 'Running' | 'Done' | 'Skipped' | 'Failed';
interface Progress { companyId: number; name: string; database: string; outcome: Outcome; message: string }

const errorText = (e: any, fallback: string) => String(e?.response?.data?.message || fallback);
const when = (v: string | null | undefined) => (v ? new Date(v).toLocaleString() : '-');

const savedChip = (s: SavedInfo | null) => {
  if (!s) return <Chip size="small" variant="outlined" label="Not saved" />;
  return s.status === 'Active'
    ? <Chip size="small" color="success" label="Saved" />
    : <Tooltip title={s.lastError || ''}><Chip size="small" color="warning" label="Not accepted by SAP" /></Tooltip>;
};

const outcomeChip = (o: Outcome) => {
  if (o === 'Running') return <CircularProgress size={16} aria-label="Working" />;
  const color = o === 'Done' ? 'success' : o === 'Failed' ? 'error' : 'default';
  const label = o === 'Done' ? 'Done' : o === 'Failed' ? 'Failed' : o === 'Skipped' ? 'Skipped' : 'Waiting';
  return <Chip size="small" color={color} variant={o === 'Pending' ? 'outlined' : 'filled'} label={label} />;
};

const SapPasswordDialog: React.FC<{
  user: UserRow;
  companies: CompanyRow[];
  technicalUser: string;
  onClose: () => void;
  onChanged: () => void;
}> = ({ user, companies, technicalUser, onClose, onChanged }) => {
  const [mode, setMode] = React.useState<Mode>('set');
  const [password, setPassword] = React.useState(() => generateSapPassword());
  const [show, setShow] = React.useState(true);
  const [copied, setCopied] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<number>>(() => new Set(companies.map((c) => c.id)));
  const [running, setRunning] = React.useState(false);
  const [progress, setProgress] = React.useState<Progress[]>([]);
  const [summary, setSummary] = React.useState<{ severity: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [history, setHistory] = React.useState<any[] | null>(null);

  const loadHistory = React.useCallback(async () => {
    try {
      const res: any = await apiService.getSapPasswordHistory(user.userId);
      setHistory(res?.data?.history || []);
    } catch {
      setHistory([]);
    }
  }, [user.userId]);

  React.useEffect(() => { void loadHistory(); }, [loadHistory]);

  const switchMode = (m: Mode) => {
    setMode(m); setCopied(false); setProgress([]); setSummary(null);
    if (m === 'set') { setPassword(generateSapPassword()); setShow(true); } else { setPassword(''); setShow(false); }
  };

  const problem = mode === 'set'
    ? (password.length < 8 || password.length > 64 ? 'Use 8 to 64 characters'
      : password !== password.trim() ? 'No space at the start or end' : '')
    : '';

  const toggle = (id: number) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const copy = async () => {
    try { await navigator.clipboard.writeText(password); setCopied(true); } catch { setCopied(false); }
  };

  const run = async () => {
    const targets = companies.filter((c) => selected.has(c.id));
    if (!targets.length || !password || problem) return;
    if (mode === 'set' && !window.confirm(
      `Set a new SAP B1 password for ${user.sapUserCode} (${user.fullname}) in ${targets.length} compan${targets.length === 1 ? 'y' : 'ies'}?\n\n`
      + 'It becomes this SAP user\'s password in every company where it is set, also for signing in to SAP B1 with a password.'
    )) return;
    setRunning(true); setSummary(null);
    const results: Progress[] = targets.map((c) => ({ companyId: c.id, name: c.name, database: c.database, outcome: 'Pending', message: '' }));
    setProgress(results.map((r) => ({ ...r })));
    const update = (i: number, patch: Partial<Progress>) => {
      results[i] = { ...results[i], ...patch };
      setProgress(results.map((r) => ({ ...r })));
    };
    let stopped = '';
    for (let i = 0; i < targets.length; i++) {
      if (stopped) { update(i, { outcome: 'Skipped', message: 'Not tried' }); continue; }
      update(i, { outcome: 'Running' });
      try {
        const res: any = await apiService.applySapPassword(user.userId, { companyId: targets[i].id, password, mode });
        const d = res?.data || {};
        update(i, { outcome: (d.outcome as Outcome) || 'Failed', message: String(d.message || '') });
      } catch (e: any) {
        const status = Number(e?.response?.status || 0);
        const text = errorText(e, 'No answer from PRPO');
        update(i, { outcome: 'Failed', message: text });
        // A refused request (password rules, protected user, no encryption key, too many changes)
        // would fail the same way in every company: stop instead of repeating it.
        if (status >= 400 && status < 500) stopped = text;
      }
    }
    setRunning(false);
    const done = results.filter((r) => r.outcome === 'Done').length;
    const failed = results.filter((r) => r.outcome === 'Failed').length;
    const skipped = results.filter((r) => r.outcome === 'Skipped').length;
    const counts = `${done} done, ${skipped} skipped, ${failed} failed`;
    if (done) {
      setSummary({
        severity: failed ? 'warning' : 'success',
        text: mode === 'set'
          ? `${counts}. The password is saved for SAP approvals on this server. On the other PRPO servers (DEV, prod, KSA), save the same password with "Already set in SAP".`
          : `${counts}. The password is saved for SAP approvals on this server.`,
      });
    } else {
      setSummary({ severity: stopped || failed ? 'error' : 'warning', text: `${counts}. Nothing was saved.${stopped ? ` ${stopped}` : ''}` });
    }
    onChanged();
    void loadHistory();
  };

  const allTicked = selected.size === companies.length;

  return (
    <ResponsiveDialog open onClose={running ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="sap-password-dialog-title">
      <DialogTitle id="sap-password-dialog-title">SAP B1 password: {user.fullname} ({user.sapUserCode})</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <RadioGroup value={mode} onChange={(e) => switchMode(e.target.value as Mode)} aria-label="What to do">
            <FormControlLabel
              value="set"
              disabled={running}
              control={<Radio />}
              label={<><strong>Set a new password in SAP</strong> and save it here</>}
            />
            <FormControlLabel
              value="save"
              disabled={running}
              control={<Radio />}
              label={<><strong>Already set in SAP</strong> (for example from another PRPO server): check it and save it here</>}
            />
          </RadioGroup>

          <Typography variant="body2" color="text.secondary">
            {mode === 'set'
              ? <>PRPO&apos;s integration user <strong>{technicalUser || '(not configured)'}</strong> sets this password in each ticked company where
                the SAP user exists, PRPO checks it with one login as {user.sapUserCode}, then saves it encrypted on this server. SAP does not
                need the old password. <strong>Copy the password</strong>: you need it to save it on the other PRPO servers.</>
              : <>PRPO logs in once as {user.sapUserCode} in each ticked company to check the password, then saves it encrypted on this
                server. SAP is not changed. Each wrong password counts toward SAP&apos;s lockout.</>}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField
              size="small"
              label={mode === 'set' ? 'New SAP B1 password' : 'SAP B1 password'}
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setCopied(false); }}
              autoComplete="new-password"
              disabled={running}
              error={!!problem && !!password}
              helperText={password ? problem || ' ' : ' '}
              fullWidth
              inputProps={{ spellCheck: false, style: { fontFamily: 'monospace' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label={show ? 'Hide the password' : 'Show the password'} onClick={() => setShow((v) => !v)} edge="end" size="small">
                      {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="outlined" onClick={copy} disabled={!password}>{copied ? 'Copied' : 'Copy'}</Button>
            {mode === 'set' ? (
              <Button onClick={() => { setPassword(generateSapPassword()); setCopied(false); setShow(true); }} disabled={running}>Suggest another</Button>
            ) : null}
          </Stack>

          <Box>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2">Companies ({selected.size} of {companies.length})</Typography>
              <Link component="button" type="button" disabled={running} onClick={() => setSelected(allTicked ? new Set() : new Set(companies.map((c) => c.id)))}>
                {allTicked ? 'Untick all' : 'Tick all'}
              </Link>
            </Stack>
            <Typography variant="caption" color="text.secondary" component="p">
              SAP keeps a password per company database: tick every company this person approves in. Companies where the SAP user does not exist are skipped.
            </Typography>
            <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, mt: 0.5 }}>
              {companies.map((c) => (
                <Box key={c.id}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={selected.has(c.id)} onChange={() => toggle(c.id)} disabled={running} />}
                    label={(
                      <Typography variant="body2" component="span">
                        {c.name} <Typography component="span" variant="caption" color="text.secondary">({c.database})</Typography>
                        {c.sapApproval ? <Chip size="small" color="info" variant="outlined" label="SAP approval" sx={{ ml: 1, height: 20 }} /> : null}
                      </Typography>
                    )}
                  />
                </Box>
              ))}
              {!companies.length ? <Typography variant="body2" sx={{ py: 1 }}>No active company with a SAP database on this server.</Typography> : null}
            </Box>
          </Box>

          <Box>
            <Button variant="contained" onClick={run} disabled={running || !selected.size || !password || !!problem}>
              {running ? 'Working…' : mode === 'set' ? 'Set in SAP and save' : 'Check and save'}
            </Button>
          </Box>

          {progress.length ? (
            <Box role="status" aria-live="polite">
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Result per company">
                  <TableBody>
                    {progress.map((p) => (
                      <TableRow key={p.companyId}>
                        <TableCell sx={{ width: 90 }}>{outcomeChip(p.outcome)}</TableCell>
                        <TableCell>
                          {p.name} <Typography component="span" variant="caption" color="text.secondary">({p.database})</Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2">{p.message}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
          {summary ? <Alert severity={summary.severity}>{summary.text}</Alert> : null}

          <Box>
            <Typography variant="subtitle2" gutterBottom>History on this server</Typography>
            {history === null ? <CircularProgress size={18} aria-label="Loading history" /> : null}
            {history && !history.length ? <Typography variant="body2" color="text.secondary">Nothing yet.</Typography> : null}
            {history && history.length ? (
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', maxHeight: 240 }}>
                <Table size="small" stickyHeader aria-label="SAP password history">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((h: any) => (
                      <TableRow key={h.logid}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(h.createddate)}</TableCell>
                        <TableCell>{h.companyname || '-'}</TableCell>
                        <TableCell>{h.action === 'Set' ? 'Set in SAP' : h.action === 'Save' ? 'Checked and saved' : 'Removed'}</TableCell>
                        <TableCell>
                          <Tooltip title={h.message || ''}>
                            <Chip size="small" color={h.outcome === 'Done' ? 'success' : 'error'} label={h.outcome} />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{h.changedby || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>Close</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

const SapUserPasswords: React.FC = () => {
  const [data, setData] = React.useState<Data | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [editing, setEditing] = React.useState<UserRow | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res: any = await apiService.getSapPasswordUsers();
      setData(res?.data || null);
    } catch (e: any) {
      setError(errorText(e, 'Could not read the SAP passwords'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const remove = async (credentialId: number, label: string) => {
    if (!window.confirm(`Remove the saved SAP password of ${label} on this server? Their next approval will ask for it again.`)) return;
    try {
      await apiService.deleteSapSavedPassword(credentialId);
      await load();
    } catch (e: any) {
      setError(errorText(e, 'Could not remove it'));
    }
  };

  const q = search.trim().toLowerCase();
  const rows = (data?.users || []).filter((u) => !q
    || u.fullname.toLowerCase().includes(q) || u.username.toLowerCase().includes(q) || u.sapUserCode.toLowerCase().includes(q));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1}>
        The SAP B1 password of every active PRPO user with a SAP user (User Management or the SAP users tab). PRPO&apos;s integration user
        {data?.technicalUser ? <> <strong>{data.technicalUser}</strong></> : null} sets it in SAP without the old password, PRPO checks it with one login
        and keeps it encrypted on <strong>this server</strong> for SAP approvals. DEV, prod and KSA share the SAP databases but each keeps its own saved
        passwords: after setting a password on one server, save the same password on the others with &quot;Already set in SAP&quot;. This list does not
        depend on the company chosen above.
      </Typography>
      {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {data && data.canSave === false ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>Saving SAP passwords is not available on this server (no encryption key), so passwords cannot be set here.</Alert>
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} mb={1.5}>
        <TextField
          size="small"
          label="Search user or SAP code"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { sm: 300 } }}
        />
        <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
        {loading ? <CircularProgress size={20} aria-label="Loading SAP passwords" /> : null}
      </Stack>

      {data ? (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="SAP B1 passwords of PRPO users">
            <TableHead>
              <TableRow>
                <TableCell>PRPO user</TableCell>
                <TableCell>SAP user</TableCell>
                <TableCell>Password on this server</TableCell>
                <TableCell>Last accepted in</TableCell>
                <TableCell>Last set or saved here</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((u) => (
                <TableRow key={u.userId}>
                  <TableCell>
                    {u.fullname}
                    <Typography variant="caption" color="text.secondary" component="div">{u.username}</Typography>
                  </TableCell>
                  <TableCell>{u.sapUserCode}</TableCell>
                  <TableCell>{u.protectedReason ? '-' : savedChip(u.saved)}</TableCell>
                  <TableCell>{u.saved?.verifiedCompany || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {u.lastChange ? (
                      <>
                        {u.lastChange.action === 'Set' ? 'Set in SAP' : 'Saved'} {when(u.lastChange.at)}
                        {u.lastChange.by ? <Typography variant="caption" color="text.secondary" component="div">by {u.lastChange.by}</Typography> : null}
                      </>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {u.protectedReason ? (
                      <Tooltip title={u.protectedReason}>
                        <Chip
                          size="small"
                          label={/integration|signs in with/.test(u.protectedReason) ? 'Integration user: not managed here' : 'SAP system account: not managed here'}
                        />
                      </Tooltip>
                    ) : (
                      <>
                        <Button size="small" variant="outlined" onClick={() => setEditing(u)} disabled={!data.canSave}>Set password</Button>
                        {u.saved ? (
                          <Button size="small" color="error" onClick={() => remove(u.saved!.credentialId, `${u.fullname} (${u.sapUserCode})`)} sx={{ ml: 1 }}>
                            Remove
                          </Button>
                        ) : null}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length ? (
                <TableRow><TableCell colSpan={6}>{q ? 'No user matches the search.' : 'No active PRPO user has a SAP user yet.'}</TableCell></TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      {data?.orphans.length ? (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>Saved passwords without an active PRPO user</Typography>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Saved passwords without an active PRPO user">
              <TableHead>
                <TableRow>
                  <TableCell>SAP user</TableCell>
                  <TableCell>Was linked to</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Saved / changed</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.orphans.map((o) => (
                  <TableRow key={o.credentialId}>
                    <TableCell>{o.sapUserCode}</TableCell>
                    <TableCell>{o.linkedName || '-'}</TableCell>
                    <TableCell>{savedChip(o)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(o.updated)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" color="error" onClick={() => remove(o.credentialId, o.sapUserCode)}>Remove</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}

      {editing && data ? (
        <SapPasswordDialog
          user={editing}
          companies={data.companies}
          technicalUser={data.technicalUser}
          onClose={() => setEditing(null)}
          onChanged={() => { void load(); }}
        />
      ) : null}
    </Box>
  );
};

export default SapUserPasswords;
